/**
 * PlayerReportRowView — one fixture this player was watched in (SE-6986,
 * relaid out in SE-8016).
 *
 * A molecule: atoms only.
 *
 * **The collapsed row is a table row, not a stack (SE-8016).** Date, fixture,
 * scout and the score chips each own a column, so a reader scanning a career
 * reads DOWN one attribute instead of re-parsing a two-line block per fixture.
 * The scout is a column of its own rather than a fragment of a subtitle — who
 * watched is the thing a scouting lead sorts by.
 *
 * **The reference block for this layout is authored in LIGHT theme and the app
 * renders dark.** Every colour here goes through a semantic token name, never
 * through the literal it resolves to in the reference: `#f5f5f5` is the card
 * ground in light and the PRIMARY TEXT colour in dark, so a transcribed hex
 * would paint text colour behind the panel. Same warning the Badge atom
 * carries — "the raw scales invert across themes".
 *
 * **The owner leads and the others show alongside, by name (AC3).** Two scouts
 * on one fixture are two independent reads; the first to evaluate owns the
 * written report, and each additional read renders as "also evaluated" with its
 * own scores. There is deliberately NO consolidated number — nothing in the
 * schema reconciles two scouts (`grep` for an agreed/final/consensus score
 * returns nothing), so a merged figure would present arithmetic as a human
 * decision nobody made. SE-8016 dropped the reference block's "Final potential
 * agreed" pill for exactly that reason; its absence is the specification.
 *
 * **The stale chip says how to act, not just that it is old (AC4).**
 * "Re-evaluate" is an instruction a scouting lead can hand out; "old" is a fact
 * they still have to interpret.
 *
 * **The score pair is labelled, and "unscored" is spelled out (SE-7237).** The
 * row used to end in a bare `4 / 4` with nothing saying which number was which,
 * and a read with neither score rendered `— / —`, which reads as a failure to
 * load rather than as the scouting fact it is: a scout noted this player and
 * never rated them. The chips carry their own labels — `Perf 8`, `Pot 9` — and
 * the unscored case says so in words instead of printing dashes.
 *
 * **The scout is always named (SE-8016).** `MatchReport.scoutUser` is
 * non-nullable and `User.displayName` is `String!`, so there is no fallback
 * here: a report cannot exist without a scout, and "Unknown scout" was a false
 * statement about data that was in the payload all along.
 * @module features/player-match-reports/components/molecules/PlayerReportRow/PlayerReportRowView
 */
import { ChevronDown, ChevronRight } from "lucide-react-native";
import React, { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_MATCH_REPORTS } from "@/e2e/testId";
import { toReportStatusTone } from "@/utils/toReportStatusLabel";

import type { PlayerEvaluationEntry } from "../../../utils/toPlayerReportRows";
import type { PillTone } from "@/components/atoms/Badge";
import type { ReportStatusKey } from "@/utils/toReportStatusLabel";

/**
 * The date column's width.
 *
 * `w-24` is 96px. The reference draws 90px, which is off the sizing ladder
 * (`w-20` is 80, `w-24` is 96) and 90 is not a tie, so it snaps UP to the
 * nearer step rather than down. Sized against the longest civil date the
 * formatter emits — "14 Feb 2026" at caption — not against the shortest.
 */
const DATE_COLUMN = "w-24";

/**
 * The scout column's width.
 *
 * The reference draws 80px against "B. Wilson". `displayName` is not always a
 * short name: its second rung is the EMAIL LOCAL PART, so this cell routinely
 * carries something like `raniel` and can carry the whole address at its third.
 * Sized one ladder step up from the reference for that case, and the text
 * truncates rather than wrapping so the row stays one line.
 */
const SCOUT_COLUMN = "w-24";

/** Chip padding, shared so the row's chips cannot drift apart. */
const CHIP_PADDING = "px-2 py-micro";

/** One chip in the collapsed row. */
interface RowChip {
  /** React key and test-id suffix. */
  readonly key: string;
  /** What it reads. */
  readonly label: string;
  /** Which ratified pill pairing it renders in. */
  readonly tone: PillTone;
}

/**
 * One score as the row prints it.
 * @param score - The score, or null/undefined when it was never given.
 * @returns The number, or an em dash.
 */
const toScoreLabel = (score: number | null | undefined): string =>
  score === null || score === undefined ? "—" : `${score}`;

/**
 * Whether the owning read carries neither score.
 *
 * A missing owner collapses with an owner who scored nothing: either way there
 * is no rating to show, and the row says "Not scored" rather than printing two
 * em dashes that look like data that failed to arrive. The old check compared
 * `owner?.performanceScore` to `null`, which is `undefined` for a null owner
 * and so fell through to the dashes it was written to prevent.
 * @param owner - The owning read, or null.
 * @returns True when there is no score to print.
 */
const isUnscored = (owner: PlayerEvaluationEntry | null): boolean =>
  owner === null ||
  (owner.performanceScore === null && owner.potentialScore === null);

/**
 * What the score column reads out to a screen reader.
 *
 * Announced as part of the row's own label rather than as separate nodes, so
 * the pair arrives with its captions attached instead of as two loose numbers.
 * @param owner - The owning read, or null.
 * @returns A sentence fragment describing the pair.
 */
const toScoreAnnouncement = (owner: PlayerEvaluationEntry | null): string =>
  isUnscored(owner)
    ? "not scored"
    : `performance ${toScoreLabel(owner?.performanceScore)}, potential ${toScoreLabel(owner?.potentialScore)}`;

/**
 * The score chips, in reading order.
 *
 * Built as data rather than as branches of JSX so the row cannot end up with a
 * different number of columns depending on which branch ran — and so the
 * unscored case swaps ONE chip in for two rather than rendering two dashes.
 *
 * There is no Fit chip. The reference block carries `Fit 68%`, but nothing on
 * `MatchReportPlayerEvaluation` backs a per-report club fit — the only
 * `fitScore` in the schema is a board-relative player metric, a different
 * measurement of a different thing. Deriving one here would put a number on a
 * scouting artefact that no scout produced, so the cell is absent rather than
 * invented (the same ruling SE-8016 made about the "agreed" potential).
 * @param owner - The owning read, or null.
 * @returns The score chips to render.
 */
const toScoreChips = (
  owner: PlayerEvaluationEntry | null
): readonly RowChip[] =>
  isUnscored(owner)
    ? [{ key: "unscored", label: "Not scored", tone: "neutral-subtle" }]
    : [
        {
          key: "perf",
          label: `Perf ${toScoreLabel(owner?.performanceScore)}`,
          tone: "neutral-subtle",
        },
        {
          key: "pot",
          label: `Pot ${toScoreLabel(owner?.potentialScore)}`,
          tone: "neutral-subtle",
        },
      ];

/**
 * The report's status as the shared vocabulary names it.
 *
 * Only filed reports reach this row, so the two server verdicts map onto the
 * two filed keys; `draft` has no row here to be.
 * @param status - The server's verdict on the report.
 * @returns The key `toReportStatusTone` and its siblings accept.
 */
const toStatusKey = (
  status: PlayerEvaluationEntry["status"]
): ReportStatusKey => (status === "PARTIAL" ? "partial" : "complete");

/**
 * The chips describing the REPORT's state rather than the player's scores.
 *
 * **The status chip's tone is not chosen here (SE-8052).** This row used to
 * spell `Complete` in the same quiet neutral the score chips wear, so a
 * workflow verdict sat beside `Perf 8` / `Pot 9` looking like a third
 * measurement — while the same `Complete` rendered green on the Reports list.
 * The tone now comes from `toReportStatusTone`, the one decision every status
 * pill in the app reads from, so this surface cannot disagree with the others
 * again.
 *
 * "Re-evaluate" says how to act, not merely that the read is old: an
 * instruction a scouting lead can hand out, where "old" is a fact they still
 * have to interpret.
 * @param owner - The owning read, or null.
 * @param isStale - Whether any read of this fixture wants redoing.
 * @returns The state chips to render.
 */
const toStateChips = (
  owner: PlayerEvaluationEntry | null,
  isStale: boolean
): readonly RowChip[] => [
  ...(owner === null
    ? []
    : [
        {
          key: "status",
          label:
            toStatusKey(owner.status) === "partial" ? "Partial" : "Complete",
          tone: toReportStatusTone(toStatusKey(owner.status)),
        },
      ]),
  ...(isStale
    ? [
        {
          key: "stale",
          label: "Re-evaluate · over 6 months old",
          tone: "warning-subtle" as const,
        },
      ]
    : []),
];

/**
 * How a read was watched, as a sentence fragment.
 * @param watchMode - LIVE, VIDEO, or null when the scout did not record it.
 * @returns " · watched live", " · watched on video", or "".
 */
const toWatchModeSuffix = (
  watchMode: PlayerEvaluationEntry["watchMode"]
): string => {
  if (watchMode === "LIVE") return " · watched live";
  if (watchMode === "VIDEO") return " · watched on video";

  return "";
};

/**
 * How a second scout watched, as the "also evaluated" sentence says it.
 * @param watchMode - LIVE, VIDEO, or null.
 * @returns " from video", " live", or "".
 */
const toWatchModePhrase = (
  watchMode: PlayerEvaluationEntry["watchMode"]
): string => {
  if (watchMode === "LIVE") return " live";
  if (watchMode === "VIDEO") return " from video";

  return "";
};

/**
 * The expanded panel's score pills, in reading order.
 *
 * `Started` is three-state on purpose: null is "the scout has not said", which
 * is not a did-not-start, so it renders nothing at all rather than asserting a
 * bench appearance. There is no minutes figure — the schema carries none, and
 * the reference's `90'` would have to be made up.
 * @param owner - The owning read.
 * @returns The pill labels.
 */
const toPanelPills = (owner: PlayerEvaluationEntry): readonly string[] => [
  `Performance ${toScoreLabel(owner.performanceScore)}`,
  `Potential ${toScoreLabel(owner.potentialScore)}`,
  ...(owner.isStarter === null
    ? []
    : [owner.isStarter ? "Started" : "Did not start"]),
];

/**
 * The scope callout's sentence, or null when there is nothing to disclaim.
 *
 * The count is DERIVED from the report's own evaluations — never a literal.
 * A report covering only this player has no other players to withhold, so the
 * callout is absent rather than reading "the other 0 players": a disclaimer
 * about nothing is noise that teaches a reader to skip the one that matters.
 * @param playerName - Whose page this is, or null when the page did not say.
 * @param otherPlayerCount - How many other players the report covers.
 * @returns The sentence, or null.
 */
const toScopeCallout = (
  playerName: string | null,
  otherPlayerCount: number
): string | null => {
  if (otherPlayerCount <= 0) return null;

  const subject =
    playerName === null || playerName.trim() === ""
      ? "this player's"
      : `${playerName}'s`;
  const others =
    otherPlayerCount === 1
      ? "The other player"
      : `The other ${otherPlayerCount} players`;

  return `You are seeing ${subject} evaluation only. ${others} in this match report are not shown on this page.`;
};

/** Props for PlayerReportRowView. */
export interface PlayerReportRowViewProps {
  /** "Sporting CP vs Benfica". */
  readonly title: string;
  /** The date line, already formatted. */
  readonly dateLabel: string;
  /** Competition, or null. */
  readonly competition: string | null;
  /** Whose page this is, for the scope callout. Null when unknown. */
  readonly playerName: string | null;
  /** The owning read — the first scout to evaluate — or null. */
  readonly owner: PlayerEvaluationEntry | null;
  /** Every additional read, shown alongside (AC3). */
  readonly others: readonly PlayerEvaluationEntry[];
  /** Whether any read wants redoing (AC4). */
  readonly isStale: boolean;
  /** Whether the row is expanded to show the evaluation. */
  readonly isExpanded: boolean;
  /** Expands or collapses. */
  readonly onToggle: () => void;
  /** Opens the full match report. Already bound to the owning report. */
  readonly onOpenOwnerReport: () => void;
  /** Test identity. */
  readonly testID?: string;
  /** Test identity for the labelled Perf/Pot pair (SE-7237). */
  readonly scoresTestID?: string;
  /** Test identity for the scout's own column (SE-8016). */
  readonly scoutTestID?: string;
  /** Test identity for the expanded panel's scope callout (SE-8016). */
  readonly scopeCalloutTestID?: string;
}

/**
 * Renders one fixture row on the player page.
 * @param root0 - Component props
 * @param root0.title - Fixture title
 * @param root0.dateLabel - Formatted date
 * @param root0.competition - Competition name
 * @param root0.playerName - Whose page this is
 * @param root0.owner - The owning read
 * @param root0.others - The additional reads
 * @param root0.isStale - Whether any read wants redoing
 * @param root0.isExpanded - Whether the evaluation is showing
 * @param root0.onToggle - Expands or collapses
 * @param root0.onOpenOwnerReport - Opens the owning report
 * @param root0.testID - Test identity
 * @param root0.scoresTestID - Test identity for the score pair
 * @param root0.scoutTestID - Test identity for the scout column
 * @param root0.scopeCalloutTestID - Test identity for the scope callout
 * @returns The row element.
 */
const PlayerReportRowView = ({
  title,
  dateLabel,
  competition,
  playerName,
  owner,
  others,
  isStale,
  isExpanded,
  onToggle,
  onOpenOwnerReport,
  testID,
  scoresTestID,
  scoutTestID,
  scopeCalloutTestID,
}: PlayerReportRowViewProps) => (
  /*
    A Stack, not a Box, so the CONTAINER owns the gap between the row and the
    panel it opens to (SE-8185).

    The panel's wrapper carried `px-4 pb-4` and no top padding, so the card's
    border began immediately after the row's own `py-3` — 12px, which read as
    the panel being part of the row rather than a block beneath it.

    `space="3"` rather than a top padding on the panel: the house rule is that a
    parent's gap owns spacing between siblings, and the gap belongs to the
    relationship between these two, not to the panel. It is also inert while the
    row is collapsed, because the second child is `null` and a gap needs two
    rendered children — a padding would have to be conditional to achieve that.

    12px matches the rhythm this file already keeps: `space="3"` between the
    panel's own blocks and `gap-3` across the row.
  */
  <Stack testID={testID} space="3" className="border-t border-outline-subtle">
    <Pressable
      onPress={onToggle}
      accessibilityRole="button"
      accessibilityState={{ expanded: isExpanded }}
      accessibilityLabel={`${title}, ${dateLabel}, ${toScoreAnnouncement(owner)}`}
      className="web:cursor-pointer web:hover:bg-surface-subtle flex-row flex-wrap items-center gap-3 px-4 py-3"
    >
      {/* The expansion affordance. Not in the reference block, kept because it
          is the only thing on the row that says the row opens — a pressable
          table row with no chevron is a table row as far as a reader knows. */}
      <Icon
        as={isExpanded ? ChevronDown : ChevronRight}
        size="sm"
        className="text-content-secondary"
      />

      <Text
        variant="caption"
        numberOfLines={1}
        className={`${DATE_COLUMN} text-content-secondary`}
      >
        {dateLabel}
      </Text>

      <Text
        variant="body"
        numberOfLines={1}
        className="min-w-32 flex-1 text-content-primary"
      >
        {title}
      </Text>

      <Text
        variant="caption"
        numberOfLines={1}
        testID={scoutTestID}
        className={`${SCOUT_COLUMN} text-content-secondary`}
      >
        {owner?.scoutLabel ?? ""}
      </Text>

      {/* The SCORE chips only. Status and staleness sit in their own group
          below, because `scoresFor` is the identity an e2e asserts a published
          score through — a status chip that ever carried a number would make
          "the page still shows the old score" pass on the wrong text. */}
      <Stack
        direction="horizontal"
        space="2"
        testID={scoresTestID}
        className="flex-wrap items-center"
      >
        {toScoreChips(owner).map(chip => (
          <TonePill
            key={chip.key}
            tone={chip.tone}
            label={chip.label}
            textVariant="micro"
            className={CHIP_PADDING}
          />
        ))}
      </Stack>

      <Stack
        direction="horizontal"
        space="2"
        className="flex-wrap items-center"
      >
        {toStateChips(owner, isStale).map(chip => (
          <TonePill
            key={chip.key}
            tone={chip.tone}
            label={chip.label}
            textVariant="micro"
            className={CHIP_PADDING}
          />
        ))}
      </Stack>
    </Pressable>

    {isExpanded && owner !== null ? (
      <Box className="px-4 pb-4">
        <Stack
          space="3"
          className="rounded-md border border-outline-subtle bg-surface-card px-5 pb-4 pt-4"
        >
          {/* 4a — who wrote it, and the way out to the whole report. */}
          <Stack direction="horizontal" space="3" className="flex-wrap">
            <Stack space="micro" className="min-w-40 flex-1">
              <Text variant="body-strong" className="text-content-primary">
                {title} · {dateLabel}
                {competition === null ? "" : ` · ${competition}`}
              </Text>
              <Text variant="caption" className="text-content-muted">
                Written by {owner.scoutLabel} · first to evaluate, owns the
                written report
                {toWatchModeSuffix(owner.watchMode)}
              </Text>
            </Stack>

            <Pressable
              onPress={onOpenOwnerReport}
              accessibilityRole="button"
              accessibilityLabel={`Open the full match report for ${title}`}
              className="web:cursor-pointer self-start"
            >
              <Text variant="caption" className="text-accent-primary">
                Open full match report ›
              </Text>
            </Pressable>
          </Stack>

          {/* 4b — the owner's numbers, spelled out rather than abbreviated. */}
          <Stack direction="horizontal" space="2" className="flex-wrap">
            {toPanelPills(owner).map(pill => (
              <Box
                key={pill}
                className="rounded-sm bg-surface-subtle px-2 py-1"
              >
                <Text variant="caption" className="text-content-secondary">
                  {pill}
                </Text>
              </Box>
            ))}
          </Stack>

          {/*
            4c — the scope callout, AFTER the grades (SE-8119).

            ONLY this player's evaluation is rendered here, which the mapper
            guarantees; this says so out loud, with a count taken off the report
            rather than written into the copy. It answers the confusion behind
            SE-8007: a reader who knows the report covered a squad reads a
            one-player panel as a truncation otherwise.

            It used to sit ABOVE the pills, which is also where the reference
            frame puts it (`ScopeCallout` y=68, `Scores` y=118). SE-8119
            diverges from the frame ON PURPOSE and this is the one place the
            divergence lives, so it is written down here rather than left to
            look like drift: a caveat printed before the numbers is a caveat
            about nothing yet — the reader meets the disclaimer, then meets the
            thing it disclaims. After the grades it lands where it is useful.

            Do not "correct" this back to the Figma order.
          */}
          {toScopeCallout(playerName, owner.otherPlayerCount) ===
          null ? null : (
            <Box
              testID={scopeCalloutTestID}
              className="rounded-md bg-status-info-surface px-3 py-2"
            >
              <Text variant="caption" className="text-status-info-strong">
                {toScopeCallout(playerName, owner.otherPlayerCount)}
              </Text>
            </Box>
          )}

          {/* 4d — the written assessment. */}
          {owner.note === null ? null : (
            <Text variant="caption" className="text-content-secondary">
              {owner.note}
            </Text>
          )}

          {/*
            4e — every additional read, each in its own box.

            AC3's wording, on purpose: an additional read shows alongside with
            its own numbers, never merged into the owner's. There is no "final"
            or "agreed" figure beside it — the reference block drew one and
            SE-8016 struck it, because no field in the schema records two scouts
            reconciling a score and a derived number under the word "agreed"
            would be arithmetic dressed as a decision.
          */}
          {others.map(entry => (
            <Stack
              key={entry.id}
              space="micro"
              testID={TEST_ID_PLAYER_MATCH_REPORTS.secondScoutFor(entry.id)}
              className="rounded-md border border-outline-subtle bg-surface-base px-3 py-2"
            >
              <Text variant="caption" className="flex-1 text-content-secondary">
                {entry.scoutLabel} also evaluated this player
                {toWatchModePhrase(entry.watchMode)} — Performance{" "}
                {toScoreLabel(entry.performanceScore)}, Potential{" "}
                {toScoreLabel(entry.potentialScore)}.
              </Text>
              {entry.note === null ? null : (
                <Text variant="micro" className="text-content-subtle">
                  {entry.note}
                </Text>
              )}
            </Stack>
          ))}
        </Stack>
      </Box>
    ) : null}
  </Stack>
);

PlayerReportRowView.displayName = "PlayerReportRowView";

export default memo(PlayerReportRowView);
